import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  useGetInvestigationChecklistQuery,
  useSaveInvestigationChecklistMutation,
} from '@/store/api/apiSlice';
import type {
  ChecklistFinding,
  ChecklistItemAnswer,
  ChecklistSection,
  MissingChecklistItem,
  SaveChecklistItem,
} from '@/types';

interface Props {
  investigationId: string;
  canEdit: boolean;
  missingItems?: MissingChecklistItem[];
  onClearMissing?: () => void;
}

// Local in-memory view model so we can drive radios + notes without round-tripping
// every keystroke.
type LocalAnswer = {
  finding: ChecklistFinding | null;
  notes: string;
};

const FINDING_LABEL: Record<ChecklistFinding, string> = {
  yes: 'Yes',
  no: 'No',
  n_a: 'N/A',
};

const FINDING_RING: Record<ChecklistFinding, string> = {
  yes: 'border-green-500 bg-green-50 text-green-700',
  no: 'border-red-500 bg-red-50 text-red-700',
  n_a: 'border-gray-400 bg-gray-50 text-gray-700',
};

function answersFromSections(sections: ChecklistSection[]): Record<string, LocalAnswer> {
  const map: Record<string, LocalAnswer> = {};
  for (const section of sections) {
    for (const item of section.items) {
      map[item.itemId] = {
        finding: item.finding,
        notes: item.notes ?? '',
      };
    }
  }
  return map;
}

export default function InvestigationChecklistPanel({
  investigationId,
  canEdit,
  missingItems = [],
  onClearMissing,
}: Props) {
  const { data: checklist, isLoading, error } = useGetInvestigationChecklistQuery(investigationId);
  const [save, { isLoading: isSaving }] = useSaveInvestigationChecklistMutation();

  const [local, setLocal] = useState<Record<string, LocalAnswer>>({});
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [saveError, setSaveError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  // Track which items have been touched since last save so we only PATCH the delta.
  const dirty = useRef<Set<string>>(new Set());
  // A live ref to the latest local state so the flush closure can read up-to-date values
  // even when invoked from an older render (e.g., recursive follow-up flush after save).
  const localRef = useRef(local);
  useEffect(() => {
    localRef.current = local;
  }, [local]);
  // Single-flight guard — only one PATCH in flight at a time.
  const isFlushing = useRef(false);
  // Refs for scroll-into-view from missingItems / errors.
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Hydrate local state when checklist arrives or refreshes.
  // Sections stay collapsed by default; the user opens them as they want.
  // IMPORTANT: merge server values with any in-flight local edits (dirty items) so a
  // refetch triggered by a successful save doesn't overwrite a question the user just
  // answered while that save was in flight.
  useEffect(() => {
    if (!checklist) return;
    setLocal((prev) => {
      const fresh = answersFromSections(checklist.sections);
      if (dirty.current.size === 0) return fresh;
      for (const id of dirty.current) {
        if (prev[id]) fresh[id] = prev[id];
      }
      return fresh;
    });
  }, [checklist?.investigationId, checklist?.answeredItems]);

  // When missingItems appear, open the affected sections and scroll to the first one.
  const missingSet = useMemo(() => new Set(missingItems.map((m) => m.itemId)), [missingItems]);
  useEffect(() => {
    if (!checklist || missingItems.length === 0) return;
    const sectionsToOpen: Record<string, boolean> = {};
    for (const section of checklist.sections) {
      if (section.items.some((item) => missingSet.has(item.itemId))) {
        sectionsToOpen[section.id] = true;
      }
    }
    setOpenSections((prev) => ({ ...prev, ...sectionsToOpen }));
    const first = missingItems[0];
    if (first) {
      // Wait a tick for the sections to mount/open.
      window.setTimeout(() => {
        const node = rowRefs.current[first.itemId];
        node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }, [missingItems, checklist?.investigationId]);

  // Stable flush routine. Snapshots the dirty set at call time, sends a PATCH for
  // exactly those items, and only removes the snapshotted ids from dirty on success.
  // Anything added to dirty during the in-flight request stays queued and triggers
  // an immediate follow-up flush. This prevents quick consecutive answers from
  // racing with each other or being clobbered by the post-save refetch.
  const flushRef = useRef<() => Promise<void>>(async () => {});
  flushRef.current = async () => {
    if (!canEdit) return;
    if (isFlushing.current) return;
    if (dirty.current.size === 0) return;

    const snapshot = Array.from(dirty.current);
    const items: SaveChecklistItem[] = [];
    for (const itemId of snapshot) {
      const a = localRef.current[itemId];
      if (!a || a.finding === null) continue;
      items.push({ itemId, finding: a.finding, ...(a.notes.trim() ? { notes: a.notes } : {}) });
    }

    if (items.length === 0) {
      snapshot.forEach((id) => dirty.current.delete(id));
      return;
    }

    isFlushing.current = true;
    setSaveError('');
    try {
      await save({ id: investigationId, data: { items } }).unwrap();
      // Only remove the items we actually saved — anything added during the request
      // remains in dirty for the follow-up flush below.
      snapshot.forEach((id) => dirty.current.delete(id));
      setLastSavedAt(Date.now());
      onClearMissing?.();
    } catch (err: any) {
      // Leave the failed items in dirty so the next change (or the follow-up below)
      // retries them.
      // Backend may surface an `outOfScopeItems` array when the agent posts an
      // ID that doesn't belong to this investigation's tier. The frontend
      // already filters via the scoped catalog so this shouldn't fire in
      // normal use — but if it does, name the offending items so the bug is
      // obvious rather than just showing a generic "failed to save".
      const oos: string[] | undefined = err?.data?.outOfScopeItems;
      const baseMessage = err?.data?.message ?? err?.message ?? 'Failed to save checklist.';
      setSaveError(
        oos && oos.length > 0
          ? `${baseMessage} (Out of scope: ${oos.join(', ')})`
          : baseMessage,
      );
    } finally {
      isFlushing.current = false;
      if (dirty.current.size > 0) {
        // Schedule a tail flush — defer one tick so React state from this turn settles.
        window.setTimeout(() => {
          void flushRef.current();
        }, 0);
      }
    }
  };

  // Debounce: flush 600ms after the last change. Sustained edits keep resetting the
  // timer, but the snapshot pattern above guarantees that whatever lands in `dirty`
  // when the timer eventually fires gets saved.
  useEffect(() => {
    if (!canEdit) return;
    if (dirty.current.size === 0) return;
    const t = window.setTimeout(() => {
      void flushRef.current();
    }, 600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, canEdit]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" />
      </div>
    );
  }

  if (error || !checklist) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
        Couldn't load the investigator checklist.
      </div>
    );
  }

  const updateAnswer = (itemId: string, patch: Partial<LocalAnswer>) => {
    if (!canEdit) return;
    setLocal((prev) => {
      const current = prev[itemId] ?? { finding: null, notes: '' };
      return { ...prev, [itemId]: { ...current, ...patch } };
    });
    dirty.current.add(itemId);
  };

  const answeredCount = Object.values(local).filter((a) => a.finding !== null).length;
  const totalCount = checklist.totalItems;

  return (
    <div className="flex flex-col gap-4">
      {/* Header bar */}
      <div className="bg-white border border-[#e0e0e0] rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-[#1b1b1b]">Investigator checklist</h3>
            {checklist.brief?.label && (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[#FF6B35]/10 text-[#FF6B35]">
                {checklist.brief.label}
              </span>
            )}
          </div>
          <p className="text-xs text-[#4e4d4d] mt-0.5">
            {checklist.brief?.summary ?? 'Answer every item before submitting the report. Changes save automatically.'}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-[#4e4d4d]">
            <span className="font-semibold text-[#1b1b1b]">{answeredCount}</span> / {totalCount} answered
          </span>
          {isSaving ? (
            <span className="inline-flex items-center gap-1 text-[#4e4d4d]">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Saving…
            </span>
          ) : lastSavedAt ? (
            <span className="inline-flex items-center gap-1 text-green-700">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Saved
            </span>
          ) : null}
        </div>
      </div>

      {/* Missing-items banner */}
      {missingItems.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold mb-1">
                {missingItems.length} item{missingItems.length === 1 ? '' : 's'} still need an answer
              </p>
              <ul className="list-disc list-inside text-xs space-y-0.5">
                {missingItems.slice(0, 5).map((m) => (
                  <li key={m.itemId} className="truncate">
                    <button
                      type="button"
                      onClick={() => rowRefs.current[m.itemId]?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                      className="text-red-700 hover:underline text-left"
                    >
                      {m.section} — {m.question}
                    </button>
                  </li>
                ))}
                {missingItems.length > 5 && (
                  <li className="text-[11px] text-red-600">+ {missingItems.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-sm text-red-700">
          {saveError}
        </div>
      )}

      {!canEdit && (
        <div className="bg-[#fafafa] border border-[#e9eaeb] rounded-xl px-4 py-3 text-xs text-[#4e4d4d]">
          Read-only view — only the assigned agent can edit this checklist.
        </div>
      )}

      {checklist.sections.map((section) => {
        const isOpen = openSections[section.id] === true;
        const sectionAnswered = section.items.filter(
          (item) => local[item.itemId]?.finding !== null && local[item.itemId]?.finding !== undefined,
        ).length;
        return (
          <div key={section.id} className="bg-white border border-[#e0e0e0] rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenSections((prev) => ({ ...prev, [section.id]: !isOpen }))}
              className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-[#fafafa] transition-colors"
            >
              <div className="flex flex-col items-start min-w-0">
                <span className="text-sm font-bold text-[#1b1b1b]">{section.title}</span>
                <span className="text-[11px] text-[#717680]">
                  {sectionAnswered} / {section.items.length} answered
                </span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-[#4e4d4d] transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && (
              <div className="border-t border-[#f0f0f0] flex flex-col">
                {section.items.map((item) => (
                  <ChecklistRow
                    key={item.itemId}
                    item={item}
                    value={local[item.itemId] ?? { finding: null, notes: '' }}
                    canEdit={canEdit}
                    isMissing={missingSet.has(item.itemId)}
                    onChange={(patch) => updateAnswer(item.itemId, patch)}
                    refSetter={(el) => {
                      rowRefs.current[item.itemId] = el;
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ChecklistRow({
  item,
  value,
  canEdit,
  isMissing,
  onChange,
  refSetter,
}: {
  item: ChecklistItemAnswer;
  value: LocalAnswer;
  canEdit: boolean;
  isMissing: boolean;
  onChange: (patch: Partial<LocalAnswer>) => void;
  refSetter: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={refSetter}
      className={`px-5 py-4 border-b border-[#f0f0f0] last:border-b-0 ${
        isMissing ? 'bg-red-50' : ''
      }`}
    >
      <p className="text-sm text-[#1b1b1b] leading-snug mb-3">{item.question}</p>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {(['yes', 'no', 'n_a'] as ChecklistFinding[]).map((f) => {
          const active = value.finding === f;
          return (
            <button
              key={f}
              type="button"
              disabled={!canEdit}
              onClick={() => onChange({ finding: f })}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                active
                  ? FINDING_RING[f]
                  : 'border-[#e0e0e0] bg-white text-[#4e4d4d] hover:bg-gray-50'
              } ${!canEdit ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              {FINDING_LABEL[f]}
            </button>
          );
        })}
      </div>
      <textarea
        value={value.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        disabled={!canEdit}
        rows={2}
        placeholder="Notes (optional)"
        className="w-full bg-white border border-[#e0e0e0] rounded-lg px-3 py-2 text-xs text-[#1b1b1b] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 resize-y disabled:bg-gray-50 disabled:cursor-not-allowed"
      />
    </div>
  );
}
